"use client";

import { FeatherDoorClosed } from "@subframe/core";
import { useQueryClient } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { toast } from "sonner";

import type { RegisteredUnitFormValues } from "@/components/registered-units/RegisteredUnitFormDialog";
import { RegisteredUnitFormDialog } from "@/components/registered-units/RegisteredUnitFormDialog";
import type {
  RegisteredUnit,
  RegisteredUnitCopy,
} from "@/components/registered-units/RegisteredUnitTable";
import { RegisteredUnitTable } from "@/components/registered-units/RegisteredUnitTable";
import {
  useCreateFireDoorCommandControllerCreate,
  useListFireDoorsQueryControllerList,
  useUpdateFireDoorCommandControllerUpdate,
} from "@/generated/fire-doors/fire-doors";

const COPY: RegisteredUnitCopy = {
  title: "Fire Doors",
  description: "Register each fire door so every inspection names the door",
  addLabel: "Add Fire Door",
  emptyTitle: "No fire doors yet",
  emptyBody:
    "Register each fire door so staff can pick the door they inspected when recording a fire door inspection.",
  emptyIcon: <FeatherDoorClosed />,
  itemNoun: "fire doors",
  errorMessage: "Error loading fire doors. Please try again.",
};

const FORM_COPY = {
  // An external or landing door may not sit in a named room.
  roomRequired: false,
  unitLabel: "Fire Door",
  namePlaceholder: "e.g., Fire Door 1 — Main Entrance",
  notesPlaceholder: "e.g., Self-closing, intumescent strips fitted",
  noRoomsHelpText: "",
};

export function FireDoorTable({ nurseryId }: { nurseryId: string }) {
  const queryClient = useQueryClient();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingUnit, setEditingUnit] = useState<RegisteredUnit | null>(null);

  const {
    data: unitsData,
    isLoading,
    error,
  } = useListFireDoorsQueryControllerList(nurseryId, {
    includeInactive: true,
  });

  const units = useMemo(() => unitsData?.data ?? [], [unitsData]);

  const invalidate = async () => {
    await queryClient.invalidateQueries({
      predicate: (query) =>
        Array.isArray(query.queryKey) &&
        typeof query.queryKey[0] === "string" &&
        query.queryKey[0].includes("/fire-doors"),
      refetchType: "all",
    });
  };

  const { mutate: createUnit, isPending: isCreating } =
    useCreateFireDoorCommandControllerCreate({
      mutation: {
        onSuccess: async () => {
          toast.success("Fire Door added successfully");
          await invalidate();
          setDialogOpen(false);
          setEditingUnit(null);
        },
        onError: (e: unknown) =>
          toast.error(
            e instanceof Error ? e.message : "Failed to add fire door",
          ),
      },
    });

  const { mutate: updateUnit, isPending: isUpdating } =
    useUpdateFireDoorCommandControllerUpdate({
      mutation: {
        onSuccess: async () => {
          await invalidate();
        },
        onError: (e: unknown) =>
          toast.error(
            e instanceof Error ? e.message : "Failed to update fire door",
          ),
      },
    });

  const handleToggleActive = (unit: RegisteredUnit) =>
    updateUnit(
      {
        nurseryId,
        fireDoorId: unit.id,
        data: { isActive: !unit.isActive },
      },
      { onSuccess: () => toast.success("Fire Door status updated") },
    );

  const handleSubmit = (values: RegisteredUnitFormValues) => {
    if (editingUnit) {
      updateUnit(
        {
          nurseryId,
          fireDoorId: editingUnit.id,
          // No room picked means "not in a room", so an edit clears it.
          data: {
            ...values,
            roomId: values.roomId ?? null,
            notes: values.notes ?? null,
          },
        },
        {
          onSuccess: () => {
            toast.success("Fire Door updated successfully");
            setDialogOpen(false);
            setEditingUnit(null);
          },
        },
      );
      return;
    }
    createUnit({ nurseryId, data: values });
  };

  return (
    <>
      <RegisteredUnitTable
        units={units}
        isLoading={isLoading}
        error={error}
        copy={COPY}
        onAdd={() => {
          setEditingUnit(null);
          setDialogOpen(true);
        }}
        onEdit={(unit) => {
          setEditingUnit(unit);
          setDialogOpen(true);
        }}
        onToggleActive={handleToggleActive}
      />
      <RegisteredUnitFormDialog
        nurseryId={nurseryId}
        unit={editingUnit}
        open={dialogOpen}
        onOpenChange={(open) => {
          setDialogOpen(open);
          if (!open) setEditingUnit(null);
        }}
        onSubmit={handleSubmit}
        isPending={isCreating || isUpdating}
        copy={FORM_COPY}
      />
    </>
  );
}
